import { computed, ref, watch } from 'vue';
import { en, type TranslationKey } from '@/i18n/resources/en.ts';
import { nb } from '@/i18n/resources/nb.ts';
import { readStoredString, writeStoredString } from '@/utils/browser-storage.ts';

export const supportedLocales = ['en', 'nb'] as const;
export type AppLocale = (typeof supportedLocales)[number];
type MessageParameters = Record<string, string | number>;
type ExtractParameterNames<Message extends string> = Message extends `${string}{${infer Parameter}}${infer Rest}`
  ? Parameter | ExtractParameterNames<Rest>
  : never;
type ParametersFor<Key extends TranslationKey> = Record<ExtractParameterNames<(typeof en)[Key]>, string | number>;
type TranslationArguments<Key extends TranslationKey> = keyof ParametersFor<Key> extends never
  ? [parameters?: MessageParameters]
  : [parameters: ParametersFor<Key>];

const STORAGE_KEY = 'vennevent:locale';
const resources = { en, nb };

export function normalizeLocale(value?: string | null): AppLocale | undefined {
  if (!value) return undefined;
  const normalized = value.toLowerCase().replace('_', '-');
  if (normalized === 'nb' || normalized === 'nn' || normalized.startsWith('no') || normalized.startsWith('nb-') || normalized.startsWith('nn-')) return 'nb';
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en';
  return undefined;
}

function initialLocale(): AppLocale {
  if (typeof window === 'undefined') return 'en';
  const stored = readStoredString({ key: STORAGE_KEY, storage: () => window.localStorage, maxBytes: 16 });
  return normalizeLocale(stored) ?? normalizeLocale(window.navigator.languages?.[0] ?? window.navigator.language) ?? 'en';
}

const locale = ref<AppLocale>(initialLocale());

function interpolate(message: string, parameters: MessageParameters) {
  return message.replace(/\{(\w+)\}/g, (match, key: string) => (key in parameters ? String(parameters[key]) : match));
}

export function useI18n() {
  function t<Key extends TranslationKey>(key: Key, ...[parameters = {}]: TranslationArguments<Key>) {
    return interpolate(resources[locale.value][key] ?? en[key], parameters);
  }

  function tc<Key extends TranslationKey>(key: Key, count: number, parameters: Omit<ParametersFor<Key>, 'count'> = {} as Omit<ParametersFor<Key>, 'count'>) {
    const choices = interpolate(resources[locale.value][key] ?? en[key], { ...parameters, count })
      .split('|')
      .map((choice) => choice.trim());
    const index = count === 0 ? 0 : count === 1 ? 1 : Math.min(2, choices.length - 1);
    return interpolate(choices[index] ?? choices[0] ?? '', { ...parameters, count });
  }

  return {
    locale,
    localeTag: computed(() => (locale.value === 'nb' ? 'nb-NO' : 'en-GB')),
    setLocale(value: AppLocale) {
      locale.value = value;
    },
    t,
    tc,
    formatDate(value: Date | string | number, options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) {
      return new Intl.DateTimeFormat(locale.value === 'nb' ? 'nb-NO' : 'en-GB', options).format(new Date(value));
    },
    formatNumber(value: number, options?: Intl.NumberFormatOptions) {
      return new Intl.NumberFormat(locale.value === 'nb' ? 'nb-NO' : 'en-GB', options).format(value);
    },
    formatDistance(kilometres: number) {
      return new Intl.NumberFormat(locale.value === 'nb' ? 'nb-NO' : 'en-GB', {
        style: 'unit',
        unit: 'kilometer',
        unitDisplay: 'short',
        maximumFractionDigits: 1,
      }).format(kilometres);
    },
    formatCurrency(value: number, currency = 'NOK') {
      return new Intl.NumberFormat(locale.value === 'nb' ? 'nb-NO' : 'en-GB', { style: 'currency', currency }).format(value);
    },
  };
}

watch(
  locale,
  (value) => {
    if (typeof document !== 'undefined') document.documentElement.lang = value;
    if (typeof window !== 'undefined') writeStoredString({ key: STORAGE_KEY, storage: () => window.localStorage, maxBytes: 16 }, value);
  },
  { immediate: true },
);
